import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Clock, Crown, Check, Share2, Copy } from "lucide-react";
import { api } from "../lib/api";
import ProgressBar from "../components/ProgressBar";
import LaunchButton from "../components/LaunchButton";
import useEmbedResize from "../hooks/useEmbedResize";

const STORE_EMAIL_KEY = "launchpad:storefront:email";
const VOTED_KEY = "launchpad:storefront:voted";

function loadVotedMap() {
  try { return JSON.parse(localStorage.getItem(VOTED_KEY) || "{}"); } catch { return {}; }
}
function saveVotedMap(map) {
  try { localStorage.setItem(VOTED_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

function timeLeft(endDate) {
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return { ended: true, label: "Round ended" };
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const secs = Math.floor((ms % (1000 * 60)) / 1000);
  if (days > 0) return { ended: false, label: `${days}d ${hours}h ${mins}m left` };
  if (hours > 0) return { ended: false, label: `${hours}h ${mins}m ${secs}s left` };
  return { ended: false, label: `${mins}m ${secs}s left` };
}

export default function StorefrontRound() {
  useEmbedResize();
  const { id } = useParams();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState(() => localStorage.getItem(STORE_EMAIL_KEY) || "");
  const [emailDraft, setEmailDraft] = useState("");
  const [voted, setVoted] = useState(() => loadVotedMap());
  const [tick, setTick] = useState(0);

  const refresh = async () => {
    try {
      const r = await api.publicGetRound(id);
      setRound(r);
    } catch {
      setRound(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [id]); // eslint-disable-line

  // Live countdown tick every second
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Refresh data every 10s while active
  useEffect(() => {
    if (!round || round.status !== "active") return;
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [round?.status]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Loading…</div>;
  if (!round) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Round not found</div>;

  const saveEmail = (e) => {
    e?.preventDefault();
    const clean = emailDraft.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error("Enter a valid email");
      return;
    }
    setEmail(clean);
    localStorage.setItem(STORE_EMAIL_KEY, clean);
    toast.success("Email saved — vote for as many as you like!");
  };

  const changeEmail = () => {
    localStorage.removeItem(STORE_EMAIL_KEY);
    setEmail("");
    setEmailDraft("");
  };

  const handleVote = async (campaignId) => {
    if (!email) {
      toast.error("Add your email at the top to vote");
      document.getElementById("storefront-round-email-input")?.focus();
      return;
    }
    try {
      const r = await api.vote(campaignId, email);
      const next = { ...voted, [campaignId]: true };
      setVoted(next);
      saveVotedMap(next);
      if (!r.ok && r.reason === "already_voted") {
        toast("You're already backing this one");
      } else {
        toast.success("You're in!");
      }
      // Optimistic update
      setRound((prev) => {
        if (!prev) return prev;
        const updated = (prev.campaigns || []).map((c) =>
          c.id === campaignId && r.ok ? { ...c, currentVoteCount: r.currentVoteCount } : c
        );
        // Re-sort by vote count desc, createdAt asc
        updated.sort((a, b) => (b.currentVoteCount - a.currentVoteCount) || a.createdAt.localeCompare(b.createdAt));
        return { ...prev, campaigns: updated };
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not vote");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Round link copied!");
  };

  const tl = timeLeft(round.endDate);
  const totalVotes = (round.campaigns || []).reduce((s, c) => s + (c.currentVoteCount || 0), 0);
  const isActive = round.status === "active";

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="storefront-round-page">
      <header className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between border-b border-gray-200">
        <Link to="/storefront" className="text-sm font-medium tracking-widest text-[#111] uppercase">Launchpad</Link>
        <Link to="/storefront" className="text-xs text-gray-500 hover:text-gray-900">← All launches</Link>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-gray-900 to-gray-700 text-white p-8 md:p-10 lp-fade-in">
          <div className="flex items-center gap-2 text-amber-200 text-xs uppercase tracking-widest">
            <Trophy size={14} /> Competitive launch
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mt-3" data-testid="round-name">
            {round.name}
          </h1>
          {round.description && (
            <p className="text-sm md:text-base text-gray-300 mt-3 max-w-2xl leading-relaxed">{round.description}</p>
          )}
          <div className="mt-6 flex items-center gap-6 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <Crown size={16} className="text-amber-300" />
              <span><strong>Top {round.winnersCount}</strong> advance to pre-order</span>
            </div>
            <div className="flex items-center gap-2" data-testid="round-countdown">
              <Clock size={16} className="text-amber-300" />
              <span className={tl.ended ? "text-amber-200" : ""}>{tl.label}</span>
            </div>
            <button onClick={copyLink} className="ml-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/20 hover:bg-white/10 transition-colors" data-testid="copy-round-link-btn">
              <Share2 size={12} /> Share
            </button>
          </div>
        </div>

        {/* Email bar */}
        <div className="lp-card p-5 mt-6" data-testid="round-email-bar">
          {email ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <Check size={16} className="text-emerald-600" />
                Voting as <strong>{email}</strong>
              </div>
              <button onClick={changeEmail} className="lp-btn-secondary text-xs">
                Change email
              </button>
            </div>
          ) : (
            <form onSubmit={saveEmail} className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <label className="lp-label" htmlFor="storefront-round-email-input">Your email</label>
                <input
                  id="storefront-round-email-input"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="lp-input"
                  data-testid="round-email-input"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                />
                <div className="lp-help">One vote per product. Save once, vote freely.</div>
              </div>
              <button type="submit" className="lp-btn-primary" data-testid="round-save-email-btn">
                Save & start voting
              </button>
            </form>
          )}
        </div>

        {/* Leaderboard — 2-per-row gallery with hero images */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="round-leaderboard-list">
          {(round.campaigns || []).map((c, i) => {
            const isWinner = i < round.winnersCount;
            const pct = totalVotes > 0 ? Math.round((c.currentVoteCount / totalVotes) * 100) : 0;
            const youVoted = !!voted[c.id];
            const closedOrPre = c.status === "pre_order" || c.status === "closed";
            return (
              <div
                key={c.id}
                data-testid={`round-product-${c.id}`}
                className={`lp-card overflow-hidden flex flex-col ${
                  isWinner && isActive ? "ring-2 ring-amber-400" : ""
                }`}
              >
                {/* Hero image with rank badge */}
                <Link to={`/storefront/${c.id}`} className="relative block bg-gray-100 aspect-square overflow-hidden group">
                  {c.productImageUrl ? (
                    <img
                      src={c.productImageUrl}
                      alt={c.productTitle}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">No image</div>
                  )}
                  {/* Rank badge */}
                  <div className={`absolute top-4 left-4 w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shadow-lg ${
                    i === 0 ? "bg-amber-500 text-white" :
                    isWinner ? "bg-amber-100 text-amber-900" :
                    "bg-white text-gray-900"
                  }`}>
                    {i + 1}
                  </div>
                  {/* Status badges - top right */}
                  <div className="absolute top-4 right-4 flex flex-col gap-1.5 items-end">
                    {isWinner && isActive && (
                      <span className="lp-badge lp-badge-preorder shadow-md">In winning zone</span>
                    )}
                    {c.status === "pre_order" && (
                      <span className="lp-badge lp-badge-preorder shadow-md">Pre-order</span>
                    )}
                    {c.status === "closed" && (
                      <span className="lp-badge lp-badge-closed shadow-md">Didn&apos;t make it</span>
                    )}
                  </div>
                </Link>

                {/* Body */}
                <div className="p-5 flex flex-col gap-3 flex-1">
                  <div>
                    <Link
                      to={`/storefront/${c.id}`}
                      className="text-lg font-semibold tracking-tight text-gray-900 hover:underline block line-clamp-2"
                    >
                      {c.productTitle}
                    </Link>
                  </div>

                  {/* Live vote progress */}
                  <div>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-sm">
                        <strong className="text-2xl font-semibold tabular-nums tracking-tight" data-testid={`round-votes-${i}`}>
                          {c.currentVoteCount}
                        </strong>
                        <span className="text-gray-500 text-xs ml-1.5">vote{c.currentVoteCount === 1 ? "" : "s"}</span>
                      </span>
                      <span className="text-xs text-gray-500 tabular-nums">{pct}% of total</span>
                    </div>
                    <ProgressBar value={c.currentVoteCount} max={Math.max(1, totalVotes)} testId={`round-progress-${i}`} />
                  </div>

                  {/* CTA */}
                  <div className="mt-auto pt-2">
                    {!isActive ? (
                      c.status === "pre_order" ? (
                        <Link to={`/storefront/${c.id}`} className="lp-btn-primary w-full justify-center" data-testid={`round-preorder-btn-${i}`}>
                          Pre-order
                        </Link>
                      ) : (
                        <button className="lp-btn-secondary w-full justify-center" disabled>Closed</button>
                      )
                    ) : closedOrPre ? (
                      <Link to={`/storefront/${c.id}`} className="lp-btn-secondary w-full justify-center">View</Link>
                    ) : (
                      <LaunchButton
                        testId={`round-vote-btn-${i}`}
                        onClick={() => handleVote(c.id)}
                        launched={youVoted}
                        label="Vote"
                        doneLabel="Voted"
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {(round.campaigns || []).length === 0 && (
            <div className="lp-card p-12 text-center text-sm text-gray-500 md:col-span-2">No products in this round yet.</div>
          )}
        </div>

        {/* How it works footer */}
        <div className="mt-10 lp-card p-6 bg-gradient-to-br from-gray-50 to-white">
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={16} className="text-amber-600" />
            <h3 className="text-sm font-semibold">How this round works</h3>
          </div>
          <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside leading-relaxed">
            <li>Vote for any of the products above — your favourites move up the leaderboard.</li>
            <li>One vote per product per email. Vote for as many as you like.</li>
            <li>When the timer hits zero, the <strong>top {round.winnersCount}</strong> become available for pre-order.</li>
            <li>We&apos;ll email every voter for the winning products with checkout instructions.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
